/**
 * 📊 SERVIÇO DE MONITORAMENTO DE GANHOS
 * Cálculo de métricas, ROI e detecção de regressões
 */

import { supabase } from '../lib/supabaseClient';
import { officialAnnualBenefitAfterOperationalUpdate } from '../components/ops/kaizen/benefitGovernance';
import { currentFieldsAfterSnapshot, realizeLeadTime } from '../utils/kaizenMetricSemantics';
import { currentAlertConditions, type OpenAlertsRead } from '../utils/regressionAlertState';
import type {
  KaizenRecord,
  MetricsSnapshot,
  RegressionAlert,
  GainsDashboardData,
  KaizenROI,
  TrendChartData,
  GainsMonitorFilters
} from '../types/gainsMonitor';

export class GainsMonitorService {
  
  // =========================================================================
  // HELPERS
  // =========================================================================
  
  /**
   * Validar se string é UUID válido
   */
  private static isValidUUID(uuid: string): boolean {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  }
  
  // =========================================================================
  // KAIZEN RECORDS
  // =========================================================================
  
  /**
   * Criar novo registro de Kaizen
   */
  static async createKaizen(data: Partial<KaizenRecord>): Promise<KaizenRecord | null> {
    try {
      const organizationId = (data as { organization_id?: string }).organization_id;
      if (!organizationId || !this.isValidUUID(organizationId)) {
        throw new Error(
          `createKaizen: organization_id inválido ou ausente ("${organizationId}"). ` +
          'Todos os registros de Kaizen devem pertencer a uma organização.',
        );
      }

      const cleanData = { ...data } as Partial<KaizenRecord> & { problem_id?: unknown };
      delete cleanData.problem_id;

      // project_id é opcional — remover silenciosamente se inválido
      if (cleanData.project_id && !this.isValidUUID(cleanData.project_id)) {
        delete cleanData.project_id;
      }

      const { data: kaizen, error } = await supabase
        .from('kaizen_records')
        .insert([{
          ...cleanData,
          status: cleanData.status || 'planejado',
          confidence_level: cleanData.confidence_level || 'media'
        }])
        .select()
        .single();

      if (error) throw error;
      
      // Criar registro de ROI vazio
      await this.initializeROI(kaizen.id);
      
      return kaizen;
    } catch (error) {
      console.error('Error creating kaizen:', error);
      return null;
    }
  }

  /**
   * Buscar Kaizens com filtros
   */
  static async getKaizens(filters?: GainsMonitorFilters): Promise<KaizenRecord[]> {
    try {
      
      let query = supabase
        .from('kaizen_records')
        .select('*')
        .order('created_at', { ascending: false });

      // 🔒 Filtrar por organização (obrigatório para isolamento multi-cliente)
      if (filters?.organization_id && this.isValidUUID(filters.organization_id)) {
        query = query.eq('organization_id', filters.organization_id);
      }

      // Validar UUID antes de aplicar filtro
      if (filters?.project_id && this.isValidUUID(filters.project_id)) {
        query = query.eq('project_id', filters.project_id);
      }

      // Filtro para stage/etapa (caso NVA venha por stage)
      if (filters?.stage_id && this.isValidUUID(filters.stage_id)) {
        query = query.eq('stage_id', filters.stage_id);
      }

      if (filters?.kaizen_type && filters.kaizen_type.length > 0) {
        query = query.in('type', filters.kaizen_type);
      }

      if (filters?.status && filters.status.length > 0) {
        query = query.in('status', filters.status);
      }

      if (filters?.responsible && filters.responsible.length > 0) {
        query = query.in('responsible', filters.responsible);
      }

      if (filters?.date_range) {
        query = query
          .gte('implementation_date', filters.date_range.start.toISOString())
          .lte('implementation_date', filters.date_range.end.toISOString());
      }

      const { data, error } = await query;

      if (error) {
        console.error('[GainsMonitor] Kaizens error:', error);
        throw error;
      }
      
      // Se filtro de alertas, buscar apenas com alertas ativos
      if (filters?.show_only_alerts) {
        const kaizenIds = data.map(k => k.id);
        const { data: alerts } = await supabase
          .from('regression_alerts')
          .select('kaizen_id')
          .in('kaizen_id', kaizenIds)
          .eq('resolved', false);
        
        const alertKaizenIds = new Set(alerts?.map(a => a.kaizen_id) || []);
        return data.filter(k => alertKaizenIds.has(k.id));
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching kaizens:', error);
      return [];
    }
  }

  static async getKaizenById(id: string): Promise<KaizenRecord | null> {
    if (!this.isValidUUID(id)) return null;
    const { data, error } = await supabase
      .from('kaizen_records')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error || !data) return null;
    return data as KaizenRecord;
  }

  /**
   * Atualizar Kaizen
   */
  static async updateKaizen(id: string, updates: Partial<KaizenRecord>): Promise<boolean> {
    try {
      const payload = { ...updates } as Partial<KaizenRecord> & { problem_id?: unknown };
      delete payload.problem_id;
      const { error } = await supabase
        .from('kaizen_records')
        .update(payload)
        .eq('id', id);

      if (error) throw error;
      
      // Atualiza sustentação e preserva annual_cost_saved.
      // A sugestão por lead time não é gravada como benefício oficial.
      if (updates.current_lead_time != null || updates.current_cost != null) {
        await this.calculateROI(id);
      }

      return true;
    } catch (error) {
      console.error('Error updating kaizen:', error);
      return false;
    }
  }

  /**
   * Deletar Kaizen
   */
  static async deleteKaizen(id: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('kaizen_records')
        .delete()
        .eq('id', id);

      return !error;
    } catch (error) {
      console.error('Error deleting kaizen:', error);
      return false;
    }
  }

  
  // =========================================================================
  // METRICS SNAPSHOTS
  // =========================================================================
  
  /**
   * Adicionar snapshot de métricas
   */
  static async addSnapshot(data: Partial<MetricsSnapshot>): Promise<MetricsSnapshot | null> {
    try {
      const { data: snapshot, error } = await supabase
        .from('metrics_snapshots')
        .insert([{
          ...data,
          measured_at: data.measured_at || new Date()
        }])
        .select()
        .single();

      if (error) throw error;

      // Atualizar métricas "current" no Kaizen
      if (snapshot) {
        await supabase
          .from('kaizen_records')
          .update(currentFieldsAfterSnapshot(snapshot))
          .eq('id', snapshot.kaizen_id);
      }

      return snapshot;
    } catch (error) {
      console.error('Error adding snapshot:', error);
      return null;
    }
  }

  /**
   * Buscar snapshots de um Kaizen
   */
  static async getSnapshots(kaizenId: string): Promise<MetricsSnapshot[]> {
    try {
      const { data, error } = await supabase
        .from('metrics_snapshots')
        .select('*')
        .eq('kaizen_id', kaizenId)
        .order('measured_at', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching snapshots:', error);
      return [];
    }
  }

  
  // =========================================================================
  // REGRESSION ALERTS
  // =========================================================================
  
  /**
   * Lê eventos não resolvidos e projeta a condição atual por Kaizen + métrica.
   * Falha de leitura não vira lista vazia.
   */
  static async getActiveAlerts(kaizenId?: string): Promise<OpenAlertsRead> {
    try {
      let query = supabase
        .from('regression_alerts')
        .select('*')
        .eq('resolved', false)
        .order('detected_at', { ascending: false });

      if (kaizenId) {
        query = query.eq('kaizen_id', kaizenId);
      }

      const { data, error } = await query;

      if (error) throw error;
      const events = (data || []) as RegressionAlert[];
      return { status: 'ok', events, conditions: currentAlertConditions(events) };
    } catch (error) {
      console.error('Error fetching alerts:', error);
      return { status: 'error' };
    }
  }

  /**
   * Reconhecer alerta
   */
  static async acknowledgeAlert(alertId: string, userId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('regression_alerts')
        .update({
          acknowledged: true,
          acknowledged_by: userId,
          acknowledged_at: new Date().toISOString()
        })
        .eq('id', alertId);

      return !error;
    } catch (error) {
      console.error('Error acknowledging alert:', error);
      return false;
    }
  }

  /**
   * Resolver alerta
   */
  static async resolveAlert(
    alertId: string,
    resolutionNotes: string
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('regression_alerts')
        .update({
          resolved: true,
          resolved_at: new Date().toISOString(),
          resolution_notes: resolutionNotes
        })
        .eq('id', alertId);

      return !error;
    } catch (error) {
      console.error('Error resolving alert:', error);
      return false;
    }
  }

  
  // =========================================================================
  // DASHBOARD DATA
  // =========================================================================
  
  /**
   * Buscar dados consolidados para o dashboard
   */
  static async getDashboardData(organizationId: string): Promise<GainsDashboardData> {
    try {
      console.log('[GainsMonitor] getDashboardData for org:', organizationId);
      
      // Se organization_id estiver vazio ou inválido, buscar estatísticas gerais
      const useGeneralStats = !organizationId || !this.isValidUUID(organizationId);
      
      if (useGeneralStats) {
        console.log('[GainsMonitor] Using general stats (no valid org ID)');
        
        // Buscar contagem direta de kaizens
        const { count: totalCount } = await supabase
          .from('kaizen_records')
          .select('id', { count: 'exact' });
        
        const { count: activeCount } = await supabase
          .from('kaizen_records')
          .select('id', { count: 'exact' })
          .in('status', ['em_implementacao', 'implementado', 'em_monitoramento']);
        
        return {
          total_kaizens: totalCount || 0,
          active_kaizens: activeCount || 0,
          sustained_kaizens: 0,
          regressed_kaizens: 0,
          total_lead_time_reduction: null,
          total_cost_savings: null,
          total_fte_freed: null,
          avg_sustainability_rate: 0,
          avg_regression_time: 0,
          active_alerts: [],
          critical_alerts_count: 0
        };
      }
      
      // Buscar dados da view consolidada
      const { data: summary, error: summaryError } = await supabase
        .from('gains_dashboard_summary')
        .select('*')
        .eq('organization_id', organizationId)
        .maybeSingle();

      if (summaryError) {
        console.error('[GainsMonitor] Summary error:', summaryError);
        throw summaryError;
      }

      // Se não encontrou summary, retornar zeros
      if (!summary) {
        console.log('[GainsMonitor] No summary found, returning zeros');
        return {
          total_kaizens: 0,
          active_kaizens: 0,
          sustained_kaizens: 0,
          regressed_kaizens: 0,
          total_lead_time_reduction: null,
          total_cost_savings: null,
          total_fte_freed: null,
          avg_sustainability_rate: 0,
          avg_regression_time: 0,
          active_alerts: [],
          critical_alerts_count: 0
        };
      }

      // Buscar alertas críticos
      const { data: criticalAlerts, error: alertsError } = await supabase
        .from('regression_alerts')
        .select(`
          *,
          kaizen_records (
            title,
            type,
            project_id
          )
        `)
        .eq('severity', 'critical')
        .eq('resolved', false)
        .order('detected_at', { ascending: false })
        .limit(10);

      if (alertsError) console.error('[GainsMonitor] Alerts error:', alertsError);

      return {
        total_kaizens: summary.total_kaizens || 0,
        active_kaizens: summary.active_kaizens || 0,
        sustained_kaizens: summary.sustained_kaizens || 0,
        regressed_kaizens: summary.regressed_kaizens || 0,
        
        total_lead_time_reduction: summary.total_lead_time_reduction ?? null,
        total_cost_savings: summary.total_cost_savings ?? null,
        total_fte_freed: summary.total_fte_freed ?? null,
        
        avg_sustainability_rate: summary.avg_sustainability_rate || 0,
        avg_regression_time: 0, // Calcular separadamente se necessário
        
        active_alerts: criticalAlerts || [],
        // Contagem da view: eventos críticos abertos, ainda sem deduplicar Kaizen + métrica.
        critical_alerts_count: summary.critical_alerts_count || 0
      };
    } catch (error) {
      console.error('[GainsMonitor] Error fetching dashboard data:', error);
      // Retornar dados zerados ao invés de null
      return {
        total_kaizens: 0,
        active_kaizens: 0,
        sustained_kaizens: 0,
        regressed_kaizens: 0,
        total_lead_time_reduction: null,
        total_cost_savings: null,
        total_fte_freed: null,
        avg_sustainability_rate: 0,
        avg_regression_time: 0,
        active_alerts: [],
        critical_alerts_count: 0
      };
    }
  }

  
  // =========================================================================
  // ROI CALCULATION
  // =========================================================================
  
  /**
   * Inicializar registro de ROI
   */
  private static async initializeROI(kaizenId: string): Promise<void> {
    try {
      await supabase
        .from('kaizen_roi')
        .insert([{
          kaizen_id: kaizenId,
          implementation_hours: 0,
          implementation_cost: 0,
          training_cost: 0,
          tool_cost: 0,
          annual_time_saved_hours: 0,
          annual_cost_saved: null,
          annual_fte_freed: 0,
          roi_percentage: 0,
          payback_months: 0,
          npv_3_years: 0,
          sustainability_rate: 100
        }]);
    } catch (error) {
      console.error('Error initializing ROI:', error);
    }
  }

  /**
   * Calcular ROI de um Kaizen
   */
  static async calculateROI(kaizenId: string): Promise<KaizenROI | null> {
    try {
      // Buscar ROI atual. Métricas do Kaizen não entram nesta conta.
      const { data: existingROI, error: roiError } = await supabase
        .from('kaizen_roi')
        .select('*')
        .eq('kaizen_id', kaizenId)
        .single();

      if (roiError && roiError.code !== 'PGRST116') throw roiError;

      // Benefício oficial permanece o valor cadastrado. Target não substitui current
      // e nenhuma heurística de lead time entra em annual_cost_saved.
      // roi_percentage e payback_months gravados são dívida histórica: a aba deriva
      // as fórmulas canônicas na leitura e esta atualização não faz backfill.
      const officialAnnual = officialAnnualBenefitAfterOperationalUpdate(existingROI?.annual_cost_saved);

      const totalInvestment = (existingROI?.implementation_cost || 0) +
                              (existingROI?.training_cost || 0) +
                              (existingROI?.tool_cost || 0);

      // Taxa gravada ainda conta eventos abertos, não condições únicas.
      // A aba de alertas projeta o estado atual sem alterar esta coluna.
      const { data: alerts } = await supabase
        .from('regression_alerts')
        .select('severity')
        .eq('kaizen_id', kaizenId)
        .eq('resolved', false);

      let sustainabilityRate = 100;
      if (alerts && alerts.length > 0) {
        const criticalCount = alerts.filter(a => a.severity === 'critical').length;
        const warningCount = alerts.filter(a => a.severity === 'warning').length;
        sustainabilityRate = Math.max(0, 100 - (criticalCount * 30) - (warningCount * 15));
      }

      // ROI ajustado por sustentação
      const roiData = {
        kaizen_id: kaizenId,
        implementation_hours: existingROI?.implementation_hours || 0,
        implementation_cost: existingROI?.implementation_cost || 0,
        training_cost: existingROI?.training_cost || 0,
        tool_cost: existingROI?.tool_cost || 0,
        annual_time_saved_hours: existingROI?.annual_time_saved_hours ?? null,
        annual_cost_saved: officialAnnual,
        annual_fte_freed: existingROI?.annual_fte_freed ?? null,
        roi_percentage: existingROI?.roi_percentage ?? null,
        payback_months: existingROI?.payback_months ?? null,
        npv_3_years: existingROI?.npv_3_years ?? null,
        sustainability_rate: sustainabilityRate,
        sustainability_adjusted_roi: existingROI?.sustainability_adjusted_roi ?? null,
      };

      const { error: updateError } = await supabase
        .from('kaizen_roi')
        .upsert([roiData]);

      if (updateError) throw updateError;

      return {
        ...roiData,
        is_positive: (officialAnnual ?? 0) > 0 && totalInvestment > 0,
        total_investment: totalInvestment,
      } as unknown as KaizenROI;

    } catch (error) {
      console.error('Error calculating ROI:', error);
      return null;
    }
  }

  /**
   * Buscar ROI de um Kaizen
   */
  static async getROI(kaizenId: string): Promise<KaizenROI | null> {
    try {
      const { data, error } = await supabase
        .from('kaizen_roi')
        .select(`
          *,
          kaizen_records (
            title
          )
        `)
        .eq('kaizen_id', kaizenId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching ROI:', error);
      return null;
    }
  }

  
  // =========================================================================
  // TREND CHARTS
  // =========================================================================
  
  /**
   * Gerar dados para gráfico de tendência
   */
  static async getTrendData(kaizenId: string): Promise<TrendChartData | null> {
    try {
      // Buscar Kaizen
      const { data: kaizen, error: kaizenError } = await supabase
        .from('kaizen_records')
        .select('*')
        .eq('id', kaizenId)
        .single();

      if (kaizenError) throw kaizenError;

      // Buscar snapshots
      const snapshots = await this.getSnapshots(kaizenId);

      return {
        kaizen_title: kaizen.title,
        baseline: kaizen.baseline_lead_time,
        target: kaizen.target_lead_time,
        snapshots: snapshots.map(s => ({
          date: new Date(s.measured_at),
          value: s.lead_time,
          is_target_met: kaizen.target_lead_time != null && s.lead_time <= kaizen.target_lead_time
        }))
      };
    } catch (error) {
      console.error('Error generating trend data:', error);
      return null;
    }
  }

  
  // =========================================================================
  // UTILITY
  // =========================================================================
  
  /**
   * Redução realizada do lead time: baseline contra a medição atual.
   * Target entra na assinatura para os callers existentes e não substitui current.
   * Ausência e baseline zero devolvem null. Zero medido em current permanece zero na conta.
   */
  static calculateGainPercentage(
    baseline: number | null | undefined,
    target: number | null | undefined,
    current: number | null | undefined,
  ): number | null {
    return realizeLeadTime(baseline, target, current).realizedReductionPercent;
  }

  /**
   * Formatar tempo em horas/minutos. Ausência não vira 0min.
   */
  static formatTime(minutes: number | null | undefined): string {
    if (typeof minutes !== 'number' || !Number.isFinite(minutes)) return '—';
    if (minutes < 60) return `${Math.round(minutes)}min`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  }

  /**
   * Formatar moeda BRL. Ausência não vira R$ 0,00.
   */
  static formatCurrency(value: number | null | undefined): string {
    if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }
}

export default GainsMonitorService;
